import { CHART_CONFIG } from './constants';
import { formatOriginalDateLabel } from './dateHelpers';

export const hexToRgba = (hex, alpha = 0.1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Cache patterns to avoid recreating them
const patternCache = new Map();

/**
 * Create a canvas pattern for chart bars (cached)
 * @param {string} color - Base color for the pattern
 * @returns {CanvasPattern} Canvas pattern object
 */
export const createPattern = (color) => {
  if (patternCache.has(color)) {
    return patternCache.get(color);
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const { PATTERN_SIZE, PATTERN_STROKE_COLOR, PATTERN_LINE_WIDTH } =
    CHART_CONFIG;

  canvas.width = PATTERN_SIZE;
  canvas.height = PATTERN_SIZE;

  // Fill with base color
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, PATTERN_SIZE, PATTERN_SIZE);

  ctx.strokeStyle = PATTERN_STROKE_COLOR;
  ctx.lineWidth = PATTERN_LINE_WIDTH;

  const pattern = ctx.createPattern(canvas, 'repeat');
  patternCache.set(color, pattern);
  return pattern;
};

export const createBarDataset = (
  label,
  color,
  data,
  backgroundColor = null,
  borderColor = null
) => {
  const dataset = {
    label,
    backgroundColor: backgroundColor ? backgroundColor : createPattern(color),
    data,
    borderWidth: CHART_CONFIG.BORDER_WIDTH,
    borderColor: borderColor || CHART_CONFIG.BORDER_COLOR,
    borderSkipped: false,
    barThickness: CHART_CONFIG.BAR_THICKNESS,
    maxBarThickness: CHART_CONFIG.MAX_BAR_THICKNESS,
    categoryPercentage: 1,
    barPercentage: 1,
  };

  return dataset;
};

export const createLineDataset = (label, color, data) => ({
  label,
  borderColor: color,
  backgroundColor: hexToRgba(color, 0.1),
  data,
  fill: false,
});

const crosshairPlugin = {
  id: 'crosshair',
  afterInit: function (chart) {
    chart.crosshair = {
      x: null,
      y: null,
      visible: false,
    };

    // Attach mouse event listeners to canvas
    const canvas = chart.canvas;

    const handleMouseMove = (e) => {
      const chartArea = chart.chartArea;
      if (!chartArea) {
        if (chart.crosshair) {
          chart.crosshair.visible = false;
        }
        chart.draw();
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if mouse is within chart area
      if (
        x >= chartArea.left &&
        x <= chartArea.right &&
        y >= chartArea.top &&
        y <= chartArea.bottom
      ) {
        // Get scales to snap to bar center
        const scales = chart.scales || {};
        const xScaleKey = Object.keys(scales).find(
          (key) => key.includes('x') || scales[key].isHorizontal
        );
        const xScale = xScaleKey ? scales[xScaleKey] : null;

        if (xScale) {
          try {
            // Get the x-axis value from mouse position
            const xValue = xScale.getValueForPixel(x);
            const xIndex = Math.max(
              0,
              Math.min(
                Math.round(xValue),
                chart.data.labels ? chart.data.labels.length - 1 : 0
              )
            );

            // Check if this bar has any data (value exists, including zero)
            let hasData = false;
            if (chart.data && chart.data.datasets) {
              for (let i = 0; i < chart.data.datasets.length; i++) {
                const dataset = chart.data.datasets[i];
                if (
                  dataset.data &&
                  dataset.data[xIndex] !== undefined &&
                  dataset.data[xIndex] !== null
                ) {
                  hasData = true;
                  break;
                }
              }
            }

            // Show crosshair if bar has data (including zero values)
            if (hasData) {
              // Get the center x position of the bar
              const barCenterX = xScale.getPixelForValue(xIndex);
              chart.crosshair.x = barCenterX;
              chart.crosshair.y = y;
              chart.crosshair.visible = true;
              chart.draw();
            } else {
              // Hide crosshair if no data at all
              if (chart.crosshair && chart.crosshair.visible) {
                chart.crosshair.visible = false;
                chart.draw();
              }
            }
          } catch (e) {
            // Fallback to original behavior if error
            chart.crosshair.x = x;
            chart.crosshair.y = y;
            chart.crosshair.visible = true;
            chart.draw();
          }
        } else {
          chart.crosshair.x = x;
          chart.crosshair.y = y;
          chart.crosshair.visible = true;
          chart.draw();
        }
      } else {
        if (chart.crosshair && chart.crosshair.visible) {
          chart.crosshair.visible = false;
          chart.draw();
        }
      }
    };

    const handleMouseLeave = () => {
      if (chart.crosshair) {
        chart.crosshair.visible = false;
        chart.draw();
      }
    };

    // Use setTimeout to ensure canvas is ready
    setTimeout(() => {
      if (canvas) {
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);

        // Store handlers for cleanup
        chart.crosshair._handlers = {
          mousemove: handleMouseMove,
          mouseleave: handleMouseLeave,
        };
      }
    }, 100);
  },
  beforeDestroy: function (chart) {
    // Clean up event listeners
    if (chart.crosshair && chart.crosshair._handlers) {
      const canvas = chart.canvas;
      if (canvas) {
        canvas.removeEventListener(
          'mousemove',
          chart.crosshair._handlers.mousemove
        );
        canvas.removeEventListener(
          'mouseleave',
          chart.crosshair._handlers.mouseleave
        );
      }
    }
  },
  afterDraw: function (chart) {
    // Check both crosshair and _crosshair (from onHover)
    const crosshairData = chart._crosshair || chart.crosshair;
    if (!crosshairData || !crosshairData.visible || crosshairData.x === null) {
      return;
    }

    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea) {
      return;
    }

    // Get scales to find bar centers
    const scales = chart.scales || {};
    const xScaleKey = Object.keys(scales).find(
      (key) => key.includes('x') || scales[key].isHorizontal
    );
    const xScale = xScaleKey ? scales[xScaleKey] : null;

    if (!xScale) {
      return;
    }

    try {
      // Get the x-axis value from mouse position
      const mouseX = crosshairData.x;
      const xValue = xScale.getValueForPixel(mouseX);
      const xIndex = Math.max(
        0,
        Math.min(
          Math.round(xValue),
          chart.data.labels ? chart.data.labels.length - 1 : 0
        )
      );

      // Check if this bar has any data (value exists, including zero)
      let hasData = false;
      if (chart.data && chart.data.datasets) {
        for (let i = 0; i < chart.data.datasets.length; i++) {
          const dataset = chart.data.datasets[i];
          if (
            dataset.data &&
            dataset.data[xIndex] !== undefined &&
            dataset.data[xIndex] !== null
          ) {
            hasData = true;
            break;
          }
        }
      }

      // Draw crosshair if bar has data (including zero values)
      if (!hasData) {
        return;
      }

      // Get the center x position of the bar
      const barCenterX = xScale.getPixelForValue(xIndex);

      // Get bar width from the first dataset's meta
      let barWidth = 0;
      if (chart.data.datasets && chart.data.datasets.length > 0) {
        const firstMeta = chart.getDatasetMeta(0);
        if (firstMeta && firstMeta.data && firstMeta.data[xIndex]) {
          const firstBar = firstMeta.data[xIndex];
          if (firstBar && firstBar._model) {
            barWidth = firstBar._model.width || 0;
          }
        }
      }

      // If we couldn't get bar width, use a default based on category percentage
      if (barWidth === 0) {
        // Calculate approximate bar width from scale
        const categoryWidth = xScale.width / (chart.data.labels?.length || 1);
        barWidth = categoryWidth * 0.8; // Approximate bar width
      }

      // Save context
      ctx.save();

      // Check chart type to determine crosshair style
      // In Chart.js v2, chart type is available via chart.config.type
      const chartType =
        chart.config?.type || (chart.chart && chart.chart.config?.type);
      const isLineChart = chartType === 'line';

      ctx.strokeStyle = 'rgba(113, 119, 132, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();

      // For both line and bar charts: draw a simple dashed vertical line
      ctx.moveTo(barCenterX, chartArea.top);
      ctx.lineTo(barCenterX, chartArea.bottom);
      ctx.stroke();

      // Reset line dash
      ctx.setLineDash([]);

      // Draw x-axis label (at top of crosshair)
      // Always use original date labels if available (for full date display from API), otherwise use formatted labels
      const originalDateLabels = chart.options?.originalDateLabels;
      let xLabel = '';

      if (originalDateLabels && originalDateLabels[xIndex]) {
        // Format the original date label to "Dec 20, 2025 time if exists" format
        xLabel = formatOriginalDateLabel(originalDateLabels[xIndex]);
      } else if (chart.data.labels && chart.data.labels[xIndex]) {
        xLabel = chart.data.labels[xIndex];
      }

      if (xLabel) {
        // Position label at the top of the crosshair line (above chart area)
        const labelHeight = 20;
        const spacing = 8; // Spacing from top of chart area

        const labelY = chartArea.top - labelHeight - spacing;
        const labelYCenter = labelY + labelHeight / 2;

        ctx.fillStyle = '#717784';
        ctx.font = '12px "Averta Bold", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const labelWidth = ctx.measureText(xLabel).width;
        const tooltipWidth = labelWidth + 10;
        const isLastBar = xIndex === chart.data.labels?.length - 1;
        const canvasRect = chart.canvas.getBoundingClientRect();
        let tooltipX = barCenterX - tooltipWidth / 2;

        if (
          isLastBar &&
          canvasRect.right - (canvasRect.left + tooltipX + tooltipWidth) < 5
        ) {
          tooltipX = barCenterX - tooltipWidth + 12;
        }

        ctx.fillStyle = '#131517';
        ctx.fillRect(tooltipX, labelY, tooltipWidth, labelHeight);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(xLabel, tooltipX + tooltipWidth / 2, labelYCenter);
      }
    } catch (e) {
      console.error('Error drawing crosshair:', e);
    }

    ctx.restore();
  },
};

export const CHART_LAYOUT = {
  padding: {
    left: -20,
    top: 35,
  },
};

export const createYAxisConfig = (
  baseYAxis,
  yAxisMax,
  yAxisStepSize,
  isStacked = false
) => ({
  ...baseYAxis,
  stacked: isStacked,
  display: true,
  gridLines: {
    display: true,
    drawBorder: false,
    color: '#F2F5F8',
    lineColor: '#F2F5F8',
    zeroLineColor: '#F2F5F8',
  },
  ticks: {
    ...baseYAxis.ticks,
    max: yAxisMax,
    min: 0,
    stepSize: yAxisStepSize,
    display: true,
    color: 'transparent',
    fontColor: 'transparent',
    padding: 8,
  },
});

export const createXAxisConfig = (baseXAxis, tickCallback) => ({
  ...baseXAxis,
  ticks: {
    ...baseXAxis.ticks,
    callback: tickCallback,
    autoSkip: false, // Don't auto-skip ticks - we handle which labels to show in the callback
    maxTicksLimit: undefined, // Remove any tick limit to show all month labels
  },
});

const barChartOptions = {
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    padding: {
      top: 35,
    },
  },
  animation: {
    duration: 0,
  },
  hover: {
    mode: 'index',
    intersect: false,
  },
  onHover: function (evt, activeElements) {
    const chart = this.chart || this;
    const canvas = chart.canvas;

    // Don't update crosshair if it's locked
    if (chart._crosshair && chart._crosshair.locked) {
      return;
    }

    if (!canvas || !chart.chartArea) {
      if (chart._crosshair) {
        chart._crosshair.visible = false;
        chart.draw();
      }
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const chartArea = chart.chartArea;

    if (
      x >= chartArea.left &&
      x <= chartArea.right &&
      y >= chartArea.top &&
      y <= chartArea.bottom
    ) {
      // Get scales to snap to bar center
      const scales = chart.scales || {};
      const xScaleKey = Object.keys(scales).find(
        (key) => key.includes('x') || scales[key].isHorizontal
      );
      const xScale = xScaleKey ? scales[xScaleKey] : null;

      if (xScale) {
        try {
          // Get the x-axis value from mouse position
          const xValue = xScale.getValueForPixel(x);
          const xIndex = Math.max(
            0,
            Math.min(
              Math.round(xValue),
              chart.data.labels ? chart.data.labels.length - 1 : 0
            )
          );

          // Check if this bar has any data (value exists, including zero)
          let hasData = false;
          if (chart.data && chart.data.datasets) {
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const dataset = chart.data.datasets[i];
              if (
                dataset.data &&
                dataset.data[xIndex] !== undefined &&
                dataset.data[xIndex] !== null
              ) {
                hasData = true;
                break;
              }
            }
          }

          // Show crosshair if bar has data (including zero values)
          if (hasData) {
            // Get the center x position of the bar
            const barCenterX = xScale.getPixelForValue(xIndex);
            chart._crosshair = {
              x: barCenterX,
              y,
              visible: true,
              locked: false,
            };
            chart.draw();
          } else {
            // Hide crosshair if no data at all
            if (chart._crosshair && chart._crosshair.visible) {
              chart._crosshair.visible = false;
              chart.draw();
            }
          }
        } catch (e) {
          // Fallback to original behavior if error
          chart._crosshair = { x, y, visible: true, locked: false };
          chart.draw();
        }
      } else {
        chart._crosshair = { x, y, visible: true, locked: false };
        chart.draw();
      }
    } else {
      if (
        chart._crosshair &&
        chart._crosshair.visible &&
        !chart._crosshair.locked
      ) {
        chart._crosshair.visible = false;
        chart.draw();
      }
    }
  },
  scales: {
    xAxes: [
      {
        stacked: true,
        gridLines: { display: false },
        ticks: {
          fontFamily: 'Averta Bold',
          fontSize: 12,
          color: '#717784',
          padding: 8,
          maxRotation: 0,
          minRotation: 0,
          // Callback will be overridden in ChartSection component
        },
      },
    ],
    yAxes: [
      {
        stacked: true,
        gridLines: {
          display: true,
          drawBorder: false,
          color: '#F2F5F8',
          lineColor: '#F2F5F8',
          zeroLineColor: '#F2F5F8',
        },
        ticks: {
          fontFamily: 'Averta Bold',
          fontSize: 12,
          color: '#717784',
          padding: 8,
          suggestedMin: 0,
          suggestedMax: 50,
          stepSize: 5,
        },
      },
    ],
  },
  legend: {
    display: false,
  },
  tooltips: {
    enabled: false,
    mode: 'index',
    intersect: false,
    backgroundColor: '#FFFFFF',
    titleFontFamily: 'Averta Bold',
    bodyFontFamily: 'Averta',
    titleFontSize: 12,
    bodyFontSize: 12,
    titleFontColor: '#131517',
    bodyFontColor: '#717784',
    borderColor: '#E1E4EA',
    borderWidth: 1,
    cornerRadius: 8,
    padding: 12,
    displayColors: true,
  },
};

const lineChartOptions = {
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    padding: {
      top: 35, // Add padding at top for crosshair tooltip
    },
  },
  hover: {
    mode: 'index',
    intersect: false,
  },
  onHover: function (evt) {
    const chart = this.chart || this;
    const canvas = chart.canvas;
    if (!canvas || !chart.chartArea) return;

    const rect = canvas.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const chartArea = chart.chartArea;

    if (
      x >= chartArea.left &&
      x <= chartArea.right &&
      y >= chartArea.top &&
      y <= chartArea.bottom
    ) {
      if (!chart._crosshair) {
        chart._crosshair = { x, y, visible: true };
      } else {
        chart._crosshair.x = x;
        chart._crosshair.y = y;
        chart._crosshair.visible = true;
      }
      // Trigger redraw to show crosshair
      chart.draw();
    } else {
      if (chart._crosshair && chart._crosshair.visible) {
        chart._crosshair.visible = false;
        chart.draw();
      }
    }
  },
  scales: {
    xAxes: [
      {
        gridLines: { display: false },
        ticks: {
          fontFamily: 'Averta Bold',
          fontSize: 12,
          color: '#717784',
          padding: 8,
          maxRotation: 0,
          minRotation: 0,
          // Callback will be overridden in ChartSection component
        },
      },
    ],
    yAxes: [
      {
        gridLines: {
          display: true,
          drawBorder: false,
          color: '#F2F5F8',
          lineColor: '#F2F5F8',
          zeroLineColor: '#F2F5F8',
        },
        ticks: {
          fontFamily: 'Averta Bold',
          fontSize: 12,
          color: '#717784',
          padding: 8,
          suggestedMin: 0,
          suggestedMax: 50,
          stepSize: 5,
        },
      },
    ],
  },
  legend: {
    display: false,
  },
  tooltips: {
    enabled: false,
  },
  elements: {
    point: {
      radius: 0,
      hoverRadius: 4,
      borderWidth: 0,
    },
    line: {
      tension: 0.4,
      borderWidth: 2,
    },
  },
};

/**
 * Calculate the appropriate Y-axis maximum value based on data
 * For stacked charts, sums values at each index to get the total height
 * Rounds up to the next nice number (multiple of 5 or 10)
 * @param {object} chartData - Chart.js data object with datasets
 * @param {boolean} isStacked - Whether the chart is stacked (default: true)
 * @returns {number} Suggested maximum Y-axis value
 */
export const calculateYAxisMax = (chartData, isStacked = true) => {
  if (!chartData?.datasets?.length) {
    return 50;
  }

  let maxValue = 0;
  const datasets = chartData.datasets;

  if (isStacked && datasets.length > 1) {
    // For stacked charts, sum values at each index
    const dataLength = datasets[0]?.data?.length || 0;
    for (let i = 0; i < dataLength; i++) {
      let sum = 0;
      for (let j = 0; j < datasets.length; j++) {
        const value = datasets[j]?.data?.[i];
        if (typeof value === 'number' && !isNaN(value)) {
          sum += value;
        }
      }
      if (sum > maxValue) maxValue = sum;
    }
  } else {
    // For non-stacked charts, find the maximum value across all datasets
    for (let i = 0; i < datasets.length; i++) {
      const data = datasets[i]?.data;
      if (Array.isArray(data)) {
        for (let j = 0; j < data.length; j++) {
          const value = data[j];
          if (typeof value === 'number' && !isNaN(value) && value > maxValue) {
            maxValue = value;
          }
        }
      }
    }
  }

  // If no data or max is 0, return default
  if (maxValue === 0) {
    return 50;
  }

  // Round up to the next nice number
  if (maxValue <= 20) {
    return Math.ceil(maxValue / 5) * 5;
  } else {
    return Math.ceil(maxValue / 10) * 10;
  }
};

/**
 * Calculate appropriate step size for Y-axis based on maximum value
 * Ensures good spacing with 5-10 grid lines and avoids tight spacing at top
 * @param {number} maxValue - Maximum Y-axis value
 * @returns {number} Step size for Y-axis ticks
 */
export const calculateYAxisStepSize = (maxValue) => {
  if (!maxValue || maxValue <= 0) {
    return 5; // Default fallback
  }

  // Target 5-8 grid lines for good readability
  const targetSteps = 6;
  const rawStep = maxValue / targetSteps;

  // Calculate step size that ensures good spacing
  // Round to nice numbers: 5, 10, 20, 25, 50, 100, etc.
  if (maxValue <= 20) {
    return 5;
  } else if (maxValue <= 50) {
    return 10;
  } else if (maxValue <= 100) {
    return 20;
  } else if (maxValue <= 150) {
    // Use 25 for better spacing when max is around 100-150
    return 25;
  } else if (maxValue <= 200) {
    return 25;
  } else if (maxValue <= 300) {
    return 50;
  } else if (maxValue <= 500) {
    return 50;
  } else if (maxValue <= 1000) {
    return 100;
  } else if (maxValue <= 2000) {
    return 200;
  } else {
    // For larger values, round to nearest nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    return Math.ceil(rawStep / magnitude) * magnitude;
  }
};

// Plugin to highlight clicked bar segment
const clickedBarHighlightPlugin = {
  id: 'clickedBarHighlight',
  afterDraw: function (chart) {
    // Get clicked bar info from chart options
    const clickedBarInfo = chart.options?.clickedBarInfo;
    if (!clickedBarInfo || !clickedBarInfo.clickedSegment) {
      return;
    }

    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    if (!chartArea || !chart.data || !chart.data.datasets) {
      return;
    }

    try {
      const dataIndex = clickedBarInfo.dataIndex;
      const clickedLabel = clickedBarInfo.clickedSegment.label;

      // Find the dataset index that matches the clicked segment label
      let datasetIndex = -1;
      for (let i = 0; i < chart.data.datasets.length; i++) {
        if (chart.data.datasets[i].label === clickedLabel) {
          datasetIndex = i;
          break;
        }
      }

      if (datasetIndex === -1 || dataIndex < 0) {
        return;
      }

      // Get the bar element for this dataset and data index
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || !meta.data || !meta.data[dataIndex]) {
        return;
      }

      const barElement = meta.data[dataIndex];
      if (!barElement || !barElement._model) {
        return;
      }

      // Get scales
      const scales = chart.scales || {};
      const xScaleKey = Object.keys(scales).find(
        (key) => key.includes('x') || scales[key].isHorizontal
      );
      const xScale = xScaleKey ? scales[xScaleKey] : null;
      const yScaleKey = Object.keys(scales).find(
        (key) => key.includes('y') || !scales[key].isHorizontal
      );
      const yScale = yScaleKey ? scales[yScaleKey] : null;

      if (!xScale || !yScale) {
        return;
      }

      ctx.save();

      // Get bar bounds
      const model = barElement._model;
      const barLeft = model.x - model.width / 2;
      const barRight = model.x + model.width / 2;
      // For stacked bars, y is top and base is bottom (accounting for inverted Y axis)
      const barTop = Math.min(model.y, model.base);
      const barBottom = Math.max(model.y, model.base);
      const barHeight = barBottom - barTop;
      const barWidth = barRight - barLeft;

      // Draw border around the clicked segment (offset slightly outside)
      ctx.strokeStyle = '#3B82F6'; // Blue color
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.beginPath();
      // Draw rectangle with slight offset to make border visible
      const borderOffset = 1.5; // Half of line width
      ctx.rect(
        barLeft - borderOffset,
        barTop - borderOffset,
        barWidth + borderOffset * 2,
        barHeight + borderOffset * 2
      );
      ctx.stroke();

      ctx.restore();
    } catch (e) {
      console.error('Error drawing clicked bar highlight:', e);
    }
  },
};

export {
  barChartOptions,
  lineChartOptions,
  crosshairPlugin,
  clickedBarHighlightPlugin,
};
